import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, ShieldCheck, Pencil, Search, Eye, EyeOff, GitMerge, RefreshCw, Copy, Grid3x3 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TEAM = ["Serdar", "Arif", "Avni", "Doğa", "Ufuk", "Recep"];

function generateStrongPassword(len = 16) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%^&*?-_=+";
  const all = upper + lower + digits + special;
  const rnd = (n) => {
    const a = new Uint32Array(1);
    window.crypto.getRandomValues(a);
    return a[0] % n;
  };
  const pick = (set) => set[rnd(set.length)];
  const chars = [pick(upper), pick(lower), pick(digits), pick(special)];
  for (let i = chars.length; i < len; i++) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rnd(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
const EMPTY_FORM = { name: "", email: "", password: "", role: "committee_manager", committees: [], allCommittees: false, committee_no: "8", agent_name: "Serdar", branches: [], directory: [], radarAccess: false, merkezApprover: false, vehicleManager: false };
const ASSIGNABLE_BRANCHES = ["Aliağa", "Antalya", "Bodrum", "Fethiye", "İskenderun", "İzmir", "Karadeniz Ereğli", "Kocaeli", "Marmaris"];

const formFromUser = (u) => ({
  name: u.name || "",
  email: u.email || "",
  password: "",
  role: u.role,
  committees: (u.committee_nos && u.committee_nos.length) ? [...u.committee_nos] : (u.committee_no != null && u.role !== "super_admin" ? [u.committee_no] : []),
  allCommittees: !!u.all_committees,
  committee_no: u.committee_no != null ? String(u.committee_no) : "8",
  agent_name: u.agent_name || "Serdar",
  branches: u.branch_names || [],
  directory: [...(u.directory_committees || [])],
  radarAccess: !!u.radar_access,
  merkezApprover: !!u.merkez_approver,
  vehicleManager: !!u.vehicle_manager,
});

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user.role === "super_admin";
  const canManage = isSuper;
  const canEditUser = (u) => canManage && !u.is_owner && (isSuper || (u.role !== "super_admin" && !(u.role === "committee_manager" && u.all_committees)));
  const [users, setUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [committeeSearch, setCommitteeSearch] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [merging, setMerging] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); api.get("/committees").then(({ data }) => setCommittees(data)); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setCommitteeSearch(""); setShowPwd(false); setOpen(true); };
  const openEdit = (u) => { setEditingId(u.id); setForm(formFromUser(u)); setCommitteeSearch(""); setShowPwd(false); setOpen(true); };

  const toggleCommittee = (no) => {
    setForm((f) => ({
      ...f,
      committees: f.committees.includes(no) ? f.committees.filter((x) => x !== no) : [...f.committees, no],
    }));
  };

  const toggleBranch = (b) => {
    setForm((f) => ({
      ...f,
      branches: f.branches.includes(b) ? f.branches.filter((x) => x !== b) : [...f.branches, b],
    }));
  };

  const save = async () => {
    if (form.role === "committee_manager" && !form.allCommittees && form.committees.length === 0) {
      toast.error("En az bir komite seçin veya 'Hepsi' işaretleyin");
      return;
    }
    if (form.role === "branch_manager" && form.branches.length === 0) {
      toast.error("En az bir şube seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role };
      if (!editingId) {
        payload.email = form.email;
        payload.password = form.password;
      } else if (form.password) {
        payload.password = form.password;
      }
      if (form.role === "super_admin") {
        payload.committee_no = null; payload.committee_nos = null; payload.all_committees = false;
      } else if (form.role === "committee_manager") {
        payload.all_committees = form.allCommittees;
        if (form.allCommittees) {
          payload.committee_nos = null; payload.committee_no = null;
        } else {
          const sel = [...form.committees].sort((a, b) => a - b);
          payload.committee_nos = sel;
          payload.committee_no = sel.length === 1 ? sel[0] : null;
        }
      } else if (form.role === "branch_manager") {
        payload.branch_names = [...form.branches];
        payload.committee_no = null; payload.committee_nos = null; payload.all_committees = false;
      } else {
        payload.committee_no = Number(form.committee_no);
        payload.committee_nos = null; payload.all_committees = false;
        payload.agent_name = form.agent_name;
      }
      payload.directory_committees = form.directory.length ? form.directory : null;
      if (isSuper) payload.radar_access = form.role === "super_admin" ? false : form.radarAccess;
      if (isSuper) payload.merkez_approver = form.role === "super_admin" ? false : form.merkezApprover;
      if (isSuper) payload.vehicle_manager = form.role === "super_admin" ? false : form.vehicleManager;
      if (editingId) await api.patch(`/users/${editingId}`, payload);      else await api.post("/users", payload);
      toast.success(editingId ? "Kullanıcı güncellendi" : "Kullanıcı oluşturuldu");
      setOpen(false); setEditingId(null); setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Kullanıcı silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doMerge = async () => {
    if (!mergeTarget || !mergeSourceId) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/users/${mergeTarget.id}/merge/${mergeSourceId}`);
      toast.success(`${data.counts.audit} işlem, ${data.counts.notes} not, ${data.counts.assignments} firma ${mergeTarget.name} hesabına devredildi`);
      setMergeTarget(null); setMergeSourceId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setMerging(false); }
  };

  const committeeSummary = (u) => {
    if (u.role === "super_admin") return "Tüm komiteler";
    if (u.role === "branch_manager") return (u.branch_names && u.branch_names.length) ? u.branch_names.join(", ") : "-";
    if (u.all_committees) return "Hepsi (Tüm komiteler)";
    const nos = (u.committee_nos && u.committee_nos.length) ? u.committee_nos : (u.committee_no != null ? [u.committee_no] : []);
    return nos.length ? nos.map((n) => `#${n}`).join(", ") : "-";
  };

  const q = committeeSearch.trim().toLocaleLowerCase("tr");
  const shownCommittees = q
    ? committees.filter((c) => `#${c.committee_no} ${c.name}`.toLocaleLowerCase("tr").includes(q))
    : committees;

  const uq = userSearch.trim().toLocaleLowerCase("tr");
  const filteredUsers = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!uq) return true;
    return `${u.name || ""} ${u.email || ""} ${committeeSummary(u)}`.toLocaleLowerCase("tr").includes(uq);
  });

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Kullanıcılar</h1>
          <p className="text-sm text-muted-foreground">Rol tabanlı erişim yönetimi (RBAC)</p>
        </div>
        <div className="flex items-center gap-2">
        {canManage && (
          <Button variant="outline" onClick={() => navigate("/yetki-matrisi")} data-testid="open-yetki-matrisi-button">
            <Grid3x3 className="h-4 w-4 mr-2" />Yetki Matrisi
          </Button>
        )}
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } }}>
          {canManage && (
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="add-user-button"><UserPlus className="h-4 w-4 mr-2" />Yeni Kullanıcı</Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı Ekle"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div className="space-y-1.5">
                <Label>Kullanıcı Adı</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ör. ahmetcan" disabled={!!editingId} data-testid="user-email" />
                {editingId && <p className="text-xs text-muted-foreground">Kullanıcı adı değiştirilemez.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Şifre</Label>
                <div className="relative">
                  <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editingId ? "Değiştirmek istemiyorsanız boş bırakın" : "Güçlü şifre (büyük/küçük/rakam/özel)"} className="pr-10" data-testid="user-password" />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-user-password" aria-label={showPwd ? "Şifreyi gizle" : "Şifreyi göster"}>
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => { const p = generateStrongPassword(16); setForm((f) => ({ ...f, password: p })); setShowPwd(true); }} data-testid="generate-password">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Güçlü Şifre Üret
                  </Button>
                  {form.password && (
                    <Button type="button" variant="ghost" size="sm" className="h-8" onClick={async () => { try { await navigator.clipboard.writeText(form.password); toast.success("Şifre kopyalandı"); } catch { toast.error("Kopyalanamadı"); } }} data-testid="copy-password">
                      <Copy className="h-3.5 w-3.5 mr-1.5" />Kopyala
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isSuper && <SelectItem value="super_admin">Genel Yönetici</SelectItem>}
                    <SelectItem value="committee_manager">Komite Yöneticisi</SelectItem>
                    <SelectItem value="branch_manager">Şube Yöneticisi</SelectItem>
                    <SelectItem value="field_agent">Saha Görevlisi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.role === "committee_manager" && (
                <div className="space-y-2">
                  <Label>Sorumlu Olduğu Meslek Komiteleri</Label>
                  {isSuper && (
                  <label className="flex items-center gap-2 rounded-md border border-border p-2.5 cursor-pointer bg-muted/40" data-testid="committee-all-row">
                    <Checkbox checked={form.allCommittees} onCheckedChange={(v) => setForm({ ...form, allCommittees: !!v })} data-testid="committee-all-checkbox" />
                    <span className="text-sm font-medium">Hepsi — tüm komiteleri ve genel durumu görsün</span>
                  </label>
                  )}
                  <div className={form.allCommittees ? "opacity-40 pointer-events-none" : ""}>
                    <div className="relative mb-1.5">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={committeeSearch} onChange={(e) => setCommitteeSearch(e.target.value)} placeholder="Komite ara..." className="pl-8 h-9" disabled={form.allCommittees} data-testid="committee-search" />
                    </div>
                    <ScrollArea className="h-52 rounded-md border border-border">
                      <div className="p-2 space-y-1">
                        {shownCommittees.map((c) => (
                          <label key={c.committee_no} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted cursor-pointer" data-testid={`committee-option-${c.committee_no}`}>
                            <Checkbox checked={form.committees.includes(c.committee_no)} disabled={form.allCommittees} onCheckedChange={() => toggleCommittee(c.committee_no)} data-testid={`committee-checkbox-${c.committee_no}`} />
                            <span className="text-sm"><span className="text-muted-foreground">#{c.committee_no}</span> {c.name}</span>
                          </label>
                        ))}
                        {shownCommittees.length === 0 && <p className="text-xs text-muted-foreground px-2 py-3">Eşleşen komite yok</p>}
                      </div>
                    </ScrollArea>
                  </div>
                  <p className="text-xs text-muted-foreground">Not: Yeni kullanıcılar seçili komitelerdeki firmaları yönetebilir.</p>
                </div>
              )}

              {form.role === "field_agent" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Komite</Label>
                    <Select value={form.committee_no} onValueChange={(v) => setForm({ ...form, committee_no: v })}>
                      <SelectTrigger data-testid="user-committee"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {committees.map((c) => <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} - {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Saha Ekip Adı (atama eşleme)</Label>
                    <Select value={form.agent_name} onValueChange={(v) => setForm({ ...form, agent_name: v })}>
                      <SelectTrigger data-testid="user-agent"><SelectValue /></SelectTrigger>
                      <SelectContent>{TEAM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Mesaj Rehberinde Görüneceği Komite(ler)</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="user-directory-chips">
                  {form.directory.length === 0 && <span className="text-xs text-muted-foreground py-1">Seçili komite yok — yetkisine göre listelenir</span>}
                  {form.directory.map((cno) => (
                    <span key={cno} className="inline-flex items-center gap-1 rounded-full bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 text-xs px-2 py-0.5" data-testid={`user-directory-chip-${cno}`}>
                      #{cno}
                      <button type="button" onClick={() => setForm({ ...form, directory: form.directory.filter((x) => x !== cno) })}
                        className="hover:text-red-500" data-testid={`user-directory-remove-${cno}`}>×</button>
                    </span>
                  ))}
                </div>
                <Select value="" onValueChange={(v) => {
                  const n = Number(v);
                  if (!form.directory.includes(n)) setForm({ ...form, directory: [...form.directory, n].sort((a, b) => a - b) });
                }}>
                  <SelectTrigger data-testid="user-directory-select"><SelectValue placeholder="Komite seçin ve ekleyin" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {committees.filter((c) => !form.directory.includes(c.committee_no)).map((c) => (
                      <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Yalnızca mesaj rehberi görünümünü etkiler, yetkiyi değiştirmez. Boş bırakılırsa sorumlu olduğu komitelere göre otomatik listelenir.</p>
              </div>

              {isSuper && form.role !== "super_admin" && (
                <label className="flex items-start gap-2 rounded-md border border-border p-2.5 cursor-pointer bg-muted/40" data-testid="radar-access-row">
                  <Checkbox checked={form.radarAccess} onCheckedChange={(v) => setForm({ ...form, radarAccess: !!v })} data-testid="radar-access-checkbox" />
                  <span className="text-sm">
                    <span className="font-medium">Radar erişimi</span>
                    <span className="block text-[11px] text-muted-foreground">Tüm komiteleri kapsayan Radar fırsatlarını görüntüleyebilir. Excel/PDF dışa aktarma kapalıdır.</span>
                  </span>
                </label>
              )}

              {isSuper && form.role !== "super_admin" && (
                <label className="flex items-start gap-2 rounded-md border border-border p-2.5 cursor-pointer bg-muted/40" data-testid="vehicle-manager-row">
                  <Checkbox checked={form.vehicleManager} onCheckedChange={(v) => setForm({ ...form, vehicleManager: !!v })} data-testid="vehicle-manager-checkbox" />
                  <span className="text-sm">
                    <span className="font-medium">Araç Sorumlusu (seçim günü)</span>
                    <span className="block text-[11px] text-muted-foreground">Seçim günü araç taleplerini görebilir, araç atayıp durumu (yolda/getirildi) güncelleyebilir.</span>
                  </span>
                </label>
              )}

              {isSuper && form.role !== "super_admin" && (
                <label className="flex items-start gap-2 rounded-md border border-border p-2.5 cursor-pointer bg-muted/40" data-testid="merkez-approver-row">
                  <Checkbox checked={form.merkezApprover} onCheckedChange={(v) => setForm({ ...form, merkezApprover: !!v })} data-testid="merkez-approver-checkbox" />
                  <span className="text-sm">
                    <span className="font-medium">Seçim Ofisi yetki belgesi onaylasın</span>
                    <span className="block text-[11px] text-muted-foreground">“Seçim Ofisine dilekçesi verildi” işaretlenen belgeleri ofiste kontrol edip onaylayabilir veya reddedebilir.</span>
                  </span>
                </label>
              )}

              {form.role === "branch_manager" && (
                <div className="space-y-2">
                  <Label>Sorumlu Olduğu Şubeler</Label>
                  <div className="rounded-md border border-border p-2 space-y-1" data-testid="branch-list">
                    {ASSIGNABLE_BRANCHES.map((b) => (
                      <label key={b} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted cursor-pointer" data-testid={`branch-option-${b}`}>
                        <Checkbox checked={form.branches.includes(b)} onCheckedChange={() => toggleBranch(b)} data-testid={`branch-checkbox-${b}`} />
                        <span className="text-sm">{b}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Şube yöneticisi, seçili şubelerdeki (tüm komiteler) firmaları görür.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="user-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? "Kaydet" : "Oluştur")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-3 border border-border">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="İsim, kullanıcı adı veya komite ara..." className="pl-9 h-9" data-testid="user-search" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 sm:w-56" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Rol: Tümü</SelectItem>
              <SelectItem value="super_admin">Genel Yönetici</SelectItem>
              <SelectItem value="committee_manager">Komite Yöneticisi</SelectItem>
              <SelectItem value="branch_manager">Şube Yöneticisi</SelectItem>
              <SelectItem value="field_agent">Saha Görevlisi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredUsers.map((u) => (
          <Card key={u.id} className="p-4 border border-border" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#0F2C59] text-white grid place-items-center font-semibold text-sm">{(u.name || "U").slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
              </div>
              {canEditUser(u) && (
                <div className="flex items-center gap-0.5">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  {isSuper && <Button variant="ghost" size="icon" className="h-8 w-8" title="Başka bir hesabın geçmişini bu hesaba devret" onClick={() => { setMergeTarget(u); setMergeSourceId(""); }} data-testid={`merge-user-${u.id}`}><GitMerge className="h-4 w-4" /></Button>}
                  {u.id !== user.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle>
                          <AlertDialogDescription>{u.name} ({u.email}) kalıcı olarak silinecek.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                          <AlertDialogAction className="bg-destructive" onClick={() => del(u.id)}>Sil</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="secondary">{ROLE_LABELS[u.role]}</Badge>
              <Badge variant="outline" data-testid={`user-committees-${u.id}`}>{committeeSummary(u)}</Badge>
              {u.agent_name && <Badge variant="outline">{u.agent_name}</Badge>}
              {u.radar_access && <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-100" data-testid={`user-radar-badge-${u.id}`}>Radar</Badge>}
              {u.merkez_approver && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid={`user-merkez-badge-${u.id}`}>Ofis Onay</Badge>}
              {u.vehicle_manager && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100" data-testid={`user-vehicle-badge-${u.id}`}>Araç Sorumlusu</Badge>}
              {u.totp_enabled && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><ShieldCheck className="h-3 w-3 mr-1" />2FA</Badge>}
              {u.is_owner && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Sistem Sahibi</Badge>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!mergeTarget} onOpenChange={(o) => { if (!o) { setMergeTarget(null); setMergeSourceId(""); } }}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader><DialogTitle className="font-display">Hesap Birleştir / Devret</DialogTitle></DialogHeader>
          {mergeTarget && (
            <div className="space-y-3 text-sm">
              <p>Seçilen kaynak hesabın <b>tüm geçmişi</b> (işlem günlüğü, arama notları, saha firma atamaları) <b>{mergeTarget.name}</b> hesabına devredilecek.</p>
              <div className="space-y-1.5">
                <Label>Kaynak hesap (geçmişi devredilir ve silinir)</Label>
                <Select value={mergeSourceId} onValueChange={setMergeSourceId}>
                  <SelectTrigger data-testid="merge-source"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((x) => x.id !== mergeTarget.id && !x.is_owner).map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.name} ({x.email}) · {ROLE_LABELS[x.role]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-amber-800 dark:text-amber-300 text-xs">
                <b>Sonuç:</b> Kaynak hesap kalıcı olarak silinir. Hedef (<b>{mergeTarget.name}</b>) Genel Yönetici + Tüm Komiteler olur. Hiçbir geçmiş kaydı silinmez, yalnızca {mergeTarget.name} hesabına aktarılır.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setMergeTarget(null); setMergeSourceId(""); }}>Vazgeç</Button>
            <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!mergeSourceId || merging} onClick={doMerge} data-testid="merge-confirm">
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Birleştir ve Devret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
